import { useState, useEffect, useCallback, useRef } from 'react';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useAnalyzeQuote, useBulkCreatePreventivi } from '../../hooks';
import { formatCurrency } from '../../utils/pricing';
import type { CmeLineItem } from '../../types';

interface UploadQuoteWizardProps {
  onClose: () => void;
  lavoroId?: string;
  lavoroNome?: string;
}

interface EditableItem extends CmeLineItem {
  selected: boolean;
}

const STEPS = ['Upload', 'Analisi', 'Revisione'] as const;
const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

export function UploadQuoteWizard({ onClose, lavoroId, lavoroNome }: UploadQuoteWizardProps) {
  const { currentProject } = useProjectContext();
  const analyzeMutation = useAnalyzeQuote();
  const bulkSaveMutation = useBulkCreatePreventivi();

  const [step, setStep] = useState(0);
  const [editableItems, setEditableItems] = useState<EditableItem[]>([]);
  const [fileData, setFileData] = useState<{ data: string; mediaType: string; filename: string } | null>(null);
  const [context, setContext] = useState(lavoroNome ? `Lavoro: ${lavoroNome}` : '');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      alert('Formato non supportato. Usa PDF, JPG, PNG o WebP.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert('File troppo grande. Massimo 8MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      setFileData({
        data: base64,
        mediaType: file.type,
        filename: file.name,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleAnalyze = useCallback(() => {
    if (!currentProject || !fileData) return;

    analyzeMutation.mutate(
      {
        projectId: currentProject.id,
        lavoroId,
        file: fileData,
        context: context || undefined,
      },
      {
        onSuccess: (result) => {
          setEditableItems(result.items.map((item) => ({ ...item, selected: true })));
          setStep(2);
        },
      }
    );
  }, [currentProject, fileData, lavoroId, context, analyzeMutation]);

  useEffect(() => {
    if (step === 1 && !analyzeMutation.isPending && !analyzeMutation.isSuccess) {
      handleAnalyze();
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleItem = (index: number) => {
    setEditableItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, selected: !item.selected } : item))
    );
  };

  const toggleAll = () => {
    const allSelected = editableItems.every((i) => i.selected);
    setEditableItems((prev) => prev.map((item) => ({ ...item, selected: !allSelected })));
  };

  const updateItemCost = (index: number, field: 'costoMin' | 'costoMed' | 'costoMax', value: string) => {
    setEditableItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: parseFloat(value) || 0 } : item))
    );
  };

  const selectedTotals = editableItems
    .filter((i) => i.selected)
    .reduce(
      (acc, item) => ({
        totaleMin: acc.totaleMin + item.costoMin,
        totaleMed: acc.totaleMed + item.costoMed,
        totaleMax: acc.totaleMax + item.costoMax,
      }),
      { totaleMin: 0, totaleMed: 0, totaleMax: 0 }
    );

  const selectedCount = editableItems.filter((i) => i.selected).length;

  const handleSave = () => {
    if (!currentProject) return;

    const items = editableItems
      .filter((i) => i.selected)
      .map((item) => ({
        categoria: item.categoria,
        voce: item.voce,
        costoMin: item.costoMin.toFixed(2),
        costoMed: item.costoMed.toFixed(2),
        costoMax: item.costoMax.toFixed(2),
        note: item.note || undefined,
      }));

    bulkSaveMutation.mutate(
      { projectId: currentProject.id, lavoroId, items },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto animate-slide-up">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Carica Preventivo</h2>
              {lavoroNome && (
                <p className="text-sm text-gray-500 mt-0.5">{lavoroNome}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={analyzeMutation.isPending}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-6">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
                    i === step
                      ? 'bg-primary-600 text-white'
                      : i < step
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {i < step ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 ${i < step ? 'bg-primary-300' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mb-4">{STEPS[step]}</p>

          {/* Step 0: Upload */}
          {step === 0 && (
            <div className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                  dragOver
                    ? 'border-primary-400 bg-primary-50'
                    : fileData
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                />
                {fileData ? (
                  <>
                    <svg className="mx-auto h-10 w-10 text-green-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-sm font-medium text-green-700">{fileData.filename}</p>
                    <p className="text-xs text-green-600 mt-1">Clicca per cambiare file</p>
                  </>
                ) : (
                  <>
                    <svg className="mx-auto h-10 w-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <p className="text-sm font-medium text-gray-700">
                      Trascina un file o clicca per selezionare
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, JPG, PNG o WebP - Max 8MB
                    </p>
                  </>
                )}
              </div>

              <div className="input-group">
                <label htmlFor="context" className="input-label">
                  Contesto aggiuntivo (opzionale)
                </label>
                <textarea
                  id="context"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  className="w-full"
                  rows={2}
                  placeholder="es. Preventivo ricevuto da Ditta Rossi per rifacimento bagno..."
                />
              </div>
            </div>
          )}

          {/* Step 1: Analisi (loading) */}
          {step === 1 && (
            <div className="flex flex-col items-center justify-center py-12">
              {analyzeMutation.isPending && (
                <>
                  <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4" />
                  <p className="text-gray-700 font-medium">Analisi in corso...</p>
                  <p className="text-sm text-gray-500 mt-1">
                    L'AI sta estraendo le voci dal documento
                  </p>
                </>
              )}
              {analyzeMutation.isError && (
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-danger-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium">Errore nell'analisi</p>
                  <p className="text-sm text-danger-600 mt-1">
                    {analyzeMutation.error?.message || 'Errore sconosciuto'}
                  </p>
                  <button
                    onClick={() => {
                      analyzeMutation.reset();
                      setStep(0);
                    }}
                    className="btn-secondary mt-4"
                  >
                    Torna indietro
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Revisione */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    Riepilogo ({selectedCount}/{editableItems.length} voci selezionate)
                  </h4>
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {editableItems.every((i) => i.selected) ? 'Deseleziona tutto' : 'Seleziona tutto'}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500">Min</p>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(selectedTotals.totaleMin)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Medio</p>
                    <p className="text-sm font-semibold text-primary-700">{formatCurrency(selectedTotals.totaleMed)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Max</p>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(selectedTotals.totaleMax)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {editableItems.map((item, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-3 transition-colors ${
                      item.selected ? 'border-primary-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => toggleItem(index)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                            {item.categoria}
                          </span>
                          <span className="text-sm font-medium text-gray-900 truncate">{item.voce}</span>
                        </div>
                        {item.note && <p className="text-xs text-gray-500 mb-2">{item.note}</p>}
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-gray-400">Min</label>
                            <input
                              type="number"
                              value={item.costoMin}
                              onChange={(e) => updateItemCost(index, 'costoMin', e.target.value)}
                              className="w-full text-xs py-1"
                              min="0"
                              step="0.01"
                              disabled={!item.selected}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400">Med</label>
                            <input
                              type="number"
                              value={item.costoMed}
                              onChange={(e) => updateItemCost(index, 'costoMed', e.target.value)}
                              className="w-full text-xs py-1"
                              min="0"
                              step="0.01"
                              disabled={!item.selected}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400">Max</label>
                            <input
                              type="number"
                              value={item.costoMax}
                              onChange={(e) => updateItemCost(index, 'costoMax', e.target.value)}
                              className="w-full text-xs py-1"
                              min="0"
                              step="0.01"
                              disabled={!item.selected}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 pt-6">
            {step === 0 && (
              <>
                <button type="button" onClick={onClose} className="btn-secondary flex-1">
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="btn-primary flex-1"
                  disabled={!fileData}
                >
                  Analizza Documento
                </button>
              </>
            )}
            {step === 2 && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    analyzeMutation.reset();
                    setStep(0);
                  }}
                  className="btn-secondary flex-1"
                >
                  Ricarica
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="btn-primary flex-1"
                  disabled={selectedCount === 0 || bulkSaveMutation.isPending}
                >
                  {bulkSaveMutation.isPending ? 'Salvataggio...' : `Salva ${selectedCount} Voci`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
